import { inject, Injectable } from '@angular/core';
import { environment } from '@mogenius/environments/environment';
import {
  AuditLogEventDto,
  AuditLogSocketEventEnum,
  ClusterEventDto,
  ClusterEventTypeEnum,
  DefaultResourceDescriptor,
  ResourceSocketEventEnum,
} from '@mogenius/client-sdk/core-dto';
import { WebsocketNamespaceEnum } from '@mogenius/client-sdk/websocket';
import { WorkspaceService } from '@mogenius/workspace/services/workspace.service';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, groupBy, mergeMap, Subject, Subscription, take } from 'rxjs';
import { MoUtils } from '@mogenius/client-sdk/utils';
import { Router } from '@angular/router';
import { NavigationService } from '@mogenius/shared/services/navigation/navigation.service';
import { KubernetesResourceService } from '@mogenius/kubernetes-resource/services/kubernetes-resource.service';
import { BaseSocketService, SocketServiceConfig } from '@mogenius/core/services/base-socket.service';

@Injectable({
  providedIn: 'root'
})
export class ClusterSocketEventService extends BaseSocketService {
  private readonly _workspaceService = inject(WorkspaceService);
  private readonly _kubernetesResourceService = inject(KubernetesResourceService);
  private readonly _router = inject(Router);
  private readonly _navigationService = inject(NavigationService);

  private _clusterChangeSubscription: Subscription | undefined = undefined;
  private _eventProcessingSubscription: Subscription | undefined = undefined;
  private _workloadYamlSubscription: Subscription | undefined = undefined;

  private _eventSubject$ = new Subject<ClusterEventDto>();
  private _auditLogEventSubject$ = new Subject<AuditLogEventDto>();

  private _currentClusterId: string | null = null;

  private static readonly SYSTEM_NAMESPACES = new Set([
    'mogenius-argo-cd',
    'mogenius-sealed-secrets',
    'mogenius',
    'flux-system'
  ]);

  private static readonly FLUX_RESOURCE_DESCRIPTORS = [
    DefaultResourceDescriptor.KustomizationResource,
    DefaultResourceDescriptor.FluxHelmReleaseResource,
    DefaultResourceDescriptor.GitRepositoryResource,
    DefaultResourceDescriptor.OciRepositoryResource,
    DefaultResourceDescriptor.HelmRepositoryResource,
    DefaultResourceDescriptor.BucketResource
  ];

  protected getConfig(): SocketServiceConfig {
    return {
      namespace: WebsocketNamespaceEnum.MO_WEBSOCKET__NAMESPACE__CLUSTER_EVENT,
      serviceName: 'Cluster WebSocket'
    };
  }

  protected setupEventHandlers(): void {
    this.socket?.on(ResourceSocketEventEnum.CHANGE_RESOURCE, (data: ClusterEventDto) => {
      this._eventSubject$.next(MoUtils.transformToDto(ClusterEventDto, data));
    });

    this.socket?.on(AuditLogSocketEventEnum.AUDIT_LOG_EVENT, (data: AuditLogEventDto) => {
      // TODO: remove debug log after verifying real-time audit log events work
      console.log('%c[Audit Log Event] received', 'color: #f59e0b; font-weight: bold', data);
      const event = MoUtils.transformToDto(AuditLogEventDto, data);
      this._auditLogEventSubject$.next(event);
    });
  }

  protected getEventNames(): string[] {
    return [ResourceSocketEventEnum.CHANGE_RESOURCE, AuditLogSocketEventEnum.AUDIT_LOG_EVENT];
  }

  public initialize(): void {
    this.initClusterChange();
    this.initEventProcessing();
  }

  private initClusterChange(): void {
    if (this._clusterChangeSubscription) {
      this._clusterChangeSubscription.unsubscribe();
    }

    this._clusterChangeSubscription = this.clusterService.selectedCluster$
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id)
      )
      .subscribe({
        next: (cluster) => {
          const newClusterId = cluster?.id || null;

          if (this._currentClusterId === newClusterId) {
            return;
          }

          this._currentClusterId = newClusterId;

          if (!cluster) {
            this.disconnect();
            return;
          }

          this.disconnect();
          this.connect();
        }
      });
  }

  private initEventProcessing(): void {
    if (this._eventProcessingSubscription) {
      this._eventProcessingSubscription.unsubscribe();
    }

    this._eventProcessingSubscription = this._eventSubject$
      .pipe(
        groupBy(
          (data: ClusterEventDto) => `${data.resource.uid}-${data.eventType}`,
          { duration: (group$) => group$.pipe(debounceTime(5000)) }
        ),
        mergeMap((group$) => group$.pipe(debounceTime(300)))
      )
      .subscribe((data: ClusterEventDto) => {
        this.resourceEvent(data);
      });
  }

  public override disconnect(): void {
    if (this._workloadYamlSubscription) {
      this._workloadYamlSubscription.unsubscribe();
      this._workloadYamlSubscription = undefined;
    }

    super.disconnect();
  }

  public teardown(): void {
    if (this._clusterChangeSubscription) {
      this._clusterChangeSubscription.unsubscribe();
      this._clusterChangeSubscription = undefined;
    }

    if (this._eventProcessingSubscription) {
      this._eventProcessingSubscription.unsubscribe();
      this._eventProcessingSubscription = undefined;
    }

    this.disconnect();
  }

  private resourceEvent(data: ClusterEventDto): void {
    // Filter events to prevent permission errors
    const isClusterScoped = this.isClusterScopedResource(data);
    const isInWorkspace = this.isInSelectedWorkspace(data);
    const isSystemNamespace = this.isSystemNamespace(data);
    const isSelected = this.isSelectedWorkload(data);

    // Debug logging
    if (!environment.production && data.resource.kind !== 'Lease' && data.resource.kind !== 'Event') {
      console.log(
        `%cresourceEvent: ${data.resource?.uid} - ${data.eventType} - ${data?.resource?.apiVersion} - ${data?.resource?.namespace} - ${data?.resource?.resourceName} | ` +
          `clusterScoped=${isClusterScoped} inWorkspace=${isInWorkspace} systemNs=${isSystemNamespace} selected=${isSelected}`,
        isClusterScoped || isInWorkspace || isSystemNamespace || isSelected
          ? 'color: green; font-style: italic'
          : 'color: red; font-style: italic'
      );
    }

    // Early return for irrelevant events from other namespaces
    if (!isClusterScoped && !isInWorkspace && !isSystemNamespace && !isSelected) {
      return;
    }

    // System namespace events go to allResource only, not to workspace
    if (isSystemNamespace && !isInWorkspace) {
      this._kubernetesResourceService.allResourceUpdateSubject$.next(data);
      return;
    }

    if (!isSelected) {
      this.clusterService.resourceUpdateSubject$.next(data);
    }

    this._kubernetesResourceService.allResourceUpdateSubject$.next(data);

    if (isInWorkspace && !isSelected) {
      this._workspaceService.resourceUpdateSubject$.next(data);
    }

    if (isSelected) {
      this.handleSelectedWorkloadEvent(data);
    }
  }

  private isClusterScopedResource(data: ClusterEventDto): boolean {
    return !data.resource.namespace || data.resource.namespace === '';
  }

  private isSystemNamespace(data: ClusterEventDto): boolean {
    return !!data.resource.namespace && ClusterSocketEventService.SYSTEM_NAMESPACES.has(data.resource.namespace);
  }

  private isInSelectedWorkspace(data: ClusterEventDto): boolean {
    const selectedWorkspace = this._workspaceService?.selectedWorkspace$?.value;

    if (!selectedWorkspace || !selectedWorkspace.resources) {
      return false;
    }

    return selectedWorkspace.resources.some((r) => {
      const ns = r.type === 'namespace' ? r.id : r.namespace;
      const groupArr = [(data?.resource as any)?.group, (data?.resource as any)?.version].filter(
        (item) => item && item.length > 0
      );
      const apiVersion = groupArr?.length > 0 ? groupArr.join('/') : data?.resource?.apiVersion;

      return (
        // Regular namespaced resources
        ns === data.resource.namespace ||
        // Namespace resource itself (special case: namespace name is in resourceName)
        (data.resource.kind === DefaultResourceDescriptor.NamespaceResource.kind &&
          data.resource.apiVersion === DefaultResourceDescriptor.NamespaceResource.apiVersion &&
          ns === data.resource.resourceName) ||
        // ArgoCD ApplicationSet - MUST match namespace to prevent permission errors
        (data.resource.kind === DefaultResourceDescriptor.ApplicationSetResource.kind &&
          apiVersion === DefaultResourceDescriptor.ApplicationSetResource.apiVersion &&
          ns === data.resource.namespace) ||
        // ArgoCD Application - MUST match namespace to prevent permission errors
        (data.resource.kind === DefaultResourceDescriptor.ApplicationResource.kind &&
          apiVersion === DefaultResourceDescriptor.ApplicationResource.apiVersion &&
          ns === data.resource.namespace) ||
        // Flux resources - MUST match namespace to prevent permission errors
        (ClusterSocketEventService.FLUX_RESOURCE_DESCRIPTORS.some(
          (descriptor) => data.resource.kind === descriptor.kind && apiVersion === descriptor.apiVersion
        ) &&
          ns === data.resource.namespace)
      );
    });
  }

  private isSelectedWorkload(data: ClusterEventDto): boolean {
    const selectedWorkload = this._kubernetesResourceService.selectedWorkload$.value;

    if (!selectedWorkload) {
      return false;
    }

    return (
      selectedWorkload.metadata?.name === data.resource.resourceName &&
      selectedWorkload.kind === data.resource.kind &&
      selectedWorkload.metadata?.namespace === data.resource.namespace
    );
  }

  private handleSelectedWorkloadEvent(data: ClusterEventDto): void {
    if (!this._kubernetesResourceService.selectedResourceDescriptor$.value) {
      return;
    }

    if (data.eventType !== ClusterEventTypeEnum.DELETE) {
      const resourceDescriptor = this._kubernetesResourceService.selectedResourceDescriptor$.value;
      const selectedWorkload = this._kubernetesResourceService.selectedWorkload$.value;

      if (resourceDescriptor && selectedWorkload?.metadata?.name && selectedWorkload?.metadata?.namespace) {
        const workloadRequest = {
          ...resourceDescriptor,
          resourceName: selectedWorkload.metadata.name,
          namespace: selectedWorkload.metadata.namespace
        };

        if (this._workloadYamlSubscription) {
          this._workloadYamlSubscription.unsubscribe();
        }

        this._workloadYamlSubscription = this._kubernetesResourceService
          .workloadYaml(workloadRequest, true)
          .pipe(
            take(1),
            catchError((err) => {
              console.error('Failed to fetch workload YAML for selected resource', err);
              return EMPTY;
            })
          )
          .subscribe();
      }
      this._kubernetesResourceService.selectedResourceUpdateSubject$.next(data);
    } else {
      this._kubernetesResourceService.selectedWorkload$.next(null);
      this._router
        .navigate(
          this._navigationService.resolveRoute([
            '/',
            'studio',
            'organization',
            '##ORGANIZATION_ID##',
            'cluster',
            '##CLUSTER_ID##',
            'workspaces',
            '##WORKSPACE_NAME##',
            'resources'
          ])
        )
        .then(() => {
          this._kubernetesResourceService.selectedResourceUpdateSubject$.next(data);
        });
    }
  }

  get eventSubject$(): Subject<ClusterEventDto> {
    return this._eventSubject$;
  }

  get auditLogEvent$(): Subject<AuditLogEventDto> {
    return this._auditLogEventSubject$;
  }
}
